import { useState, useEffect, useCallback } from 'react';
import {
  Ticket,
  TicketComment,
  CreateTicketRequest,
  UpdateTicketRequest,
  CreateCommentRequest,
  TicketStats,
  PaginatedTickets,
  TicketAttachment,
  UpdateCommentRequest
} from '../types/ticket';
import { ticketService } from '../services/tickets';

export const useTickets = () => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    per_page: 10,
    total_pages: 0,
  });

  const fetchTickets = async (params?: {
    page?: number;
    per_page?: number;
    status?: string;
    priority?: string;
    category?: string;
    assigned_to_id?: number;
    company_id?: number;
  }) => {
    setLoading(true);
    setError(null);

    try {
      const response = await ticketService.getTickets(params);
      setTickets(response.tickets);
      setPagination({
        total: response.total,
        page: response.page,
        per_page: response.per_page,
        total_pages: response.total_pages,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar tickets');
    } finally {
      setLoading(false);
    }
  };

  const createTicket = async (ticketData: CreateTicketRequest): Promise<Ticket | null> => {
    setLoading(true);
    setError(null);

    try {
      const newTicket = await ticketService.createTicket(ticketData);
      setTickets(prev => [newTicket, ...prev]);
      return newTicket;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao criar ticket');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateTicket = async (id: number, ticketData: UpdateTicketRequest): Promise<Ticket | null> => {
    setLoading(true);
    setError(null);

    try {
      const updatedTicket = await ticketService.updateTicket(id, ticketData);
      setTickets(prev => prev.map(ticket => (ticket.id === id ? updatedTicket : ticket)));
      return updatedTicket;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao atualizar ticket');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteTicket = async (id: number): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      await ticketService.deleteTicket(id);
      setTickets(prev => prev.filter(t => t.id !== id));
      // Atualiza paginação total se necessário
      setPagination(prev => ({
        ...prev,
        total: Math.max(0, prev.total - 1),
        total_pages: prev.total_pages // manter, backend calcula geralmente
      }));
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir ticket');
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    tickets,
    loading,
    error,
    pagination,
    fetchTickets,
    createTicket,
    updateTicket,
    deleteTicket
  };
};

export const useTicket = (id: number) => {
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [comments, setComments] = useState<TicketComment[]>([]);
  const [attachments, setAttachments] = useState<TicketAttachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTicket = async () => {
    setLoading(true);
    setError(null);

    try {
      const ticketData = await ticketService.getTicket(id);
      setTicket(ticketData);
      setAttachments(ticketData.attachments || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar ticket');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const commentsData = await ticketService.getTicketComments(id);
      setComments(commentsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar comentários');
    }
  };

  const fetchAttachments = async () => {
    try {
      const data = await ticketService.listAttachments(id);
      setAttachments(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar anexos');
    }
  };

  const addComment = async (commentData: CreateCommentRequest): Promise<TicketComment | null> => {
    try {
      const newComment = await ticketService.createComment(id, commentData);
      setComments(prev => [...prev, newComment]);
      return newComment;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao adicionar comentário');
      return null;
    }
  };

  const uploadAttachments = async (files: File[], commentId?: number) => {
    try {
      const uploadedAttachments: TicketAttachment[] = await ticketService.uploadAttachments(id, files, commentId);
      const newAttachments = uploadedAttachments;
      setAttachments(prev => [...prev, ...newAttachments]);
      // Refresh ticket to update timestamps
      await fetchTicket();
      return newAttachments;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao enviar anexos');
      return null;
    }
  };

  const updateTicket = async (ticketData: UpdateTicketRequest): Promise<Ticket | null> => {
    setLoading(true);
    setError(null);

    try {
      const updatedTicket = await ticketService.updateTicket(id, ticketData);
      setTicket(updatedTicket);
      return updatedTicket;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao atualizar ticket');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateComment = async (commentId: number, data: UpdateCommentRequest): Promise<TicketComment | null> => {
    try {
      const updated = await ticketService.updateComment(id, commentId, data);
      setComments(prev => prev.map(c => c.id === commentId ? updated : c));
      // Refresh ticket metadata (e.g., updated_at)
      await fetchTicket();
      return updated;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao atualizar comentário');
      return null;
    }
  };

  const deleteComment = async (commentId: number): Promise<boolean> => {
    try {
      await ticketService.deleteComment(id, commentId);
      setComments(prev => prev.filter(c => c.id !== commentId));
      // Attachments may be affected depending on backend; refresh to be safe
      await fetchAttachments();
      await fetchTicket();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir comentário');
      return false;
    }
  };

  const deleteAttachment = async (attachmentId: number): Promise<boolean> => {
    try {
      await ticketService.deleteAttachment(id, attachmentId);
      setAttachments(prev => prev.filter(a => a.id !== attachmentId));
      await fetchTicket();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir anexo');
      return false;
    }
  };

  const deleteTicket = async (): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      await ticketService.deleteTicket(id);
      setTicket(null);
      setComments([]);
      setAttachments([]);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir ticket');
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchTicket();
      fetchComments();
      fetchAttachments();
    }
  }, [id]);

  return {
    ticket,
    comments,
    attachments,
    loading,
    error,
    fetchTicket,
    fetchComments,
    fetchAttachments,
    addComment,
    uploadAttachments,
    updateTicket,
    updateComment,
    deleteComment,
    deleteAttachment,
    deleteTicket
  };
};

export const useTicketStats = (companyId?: number) => {
  const [stats, setStats] = useState<TicketStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const statsData = await ticketService.getTicketStats(companyId);
      setStats(statsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    fetchStats,
  };
};
